<template>
  <Dialog
    v-model="dialogVisible"
    title="Edit Patient Prices by Batch"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    @close="handleClose"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>
    </template>

    <!-- Dialog Content -->
    <div class="batch-edit-body" :class="{ 'is-saving': saving }">
      <!-- Choose Provider Section (Clinic Only) -->
      <div v-if="isClinic" class="section provider-section">
        <div class="form-field">
          <label class="field-label">
            Edit Patient Price for <span class="required">(Required)</span>
          </label>
          <MultiSelector
            v-model="selectedProviderValues"
            size="medium"
            placeholder="Search providers..."
            :options="providerOptions"
          />
        </div>

        <!-- Allow Customization Toggle -->
        <div class="form-field customization-field">
          <div class="customization-row">
            <label class="field-label customization-label">Allow Customization</label>
            <div class="toggle-group">
              <span class="toggle-label" :class="{ active: !allowCustomization }">Do not Allow Customization</span>
              <el-switch
                v-model="allowCustomization"
                class="customization-switch"
              />
              <span class="toggle-label" :class="{ active: allowCustomization }">Allow Customization</span>
            </div>
          </div>
        </div>

        <!-- Info Notification -->
        <InlineNotification
          v-if="showCustomizationInfo"
          theme="info"
          class="info-alert"
          message="If customization is not allowed, providers cannot change this price on their own."
          :with_close_btn="true"
          @close="showCustomizationInfo = false"
        />
      </div>

      <!-- Divider -->
      <div v-if="isClinic" class="section-divider"></div>

      <!-- Price Update Section -->
      <div class="section price-section">
        <div class="form-field">
          <label class="field-label">Price Update Type</label>
          <SingleSelector
            v-model="priceUpdateType"
            size="medium"
            :options="batchPriceUpdateOptions"
            placeholder="Select price update type"
            @change="handlePriceTypeChange"
          />
        </div>

        <!-- Dynamic Input Field based on Price Update Type -->
        <div v-if="priceUpdateType !== 'no_markup'" class="form-field">
          <label class="field-label">{{ priceInputLabel }}</label>
          <TextInput
            v-model="priceValue"
            size="medium"
            :placeholder="priceInputPlaceholder"
            :clearable="true"
            :class="{ 'price-input-error': priceError }"
          >
            <template v-if="currentPrefix" #prefix>
              <span class="price-input-prefix">{{ currentPrefix }}</span>
            </template>
            <template v-if="priceUpdateType === 'added_percentage'" #suffix>
              <span class="price-input-suffix">%</span>
            </template>
          </TextInput>
        </div>

        <!-- Price Error Notification -->
        <InlineNotification
          v-if="priceError"
          theme="error"
          class="error-alert"
          message="The retail price must be set between 100% and 150% of the wholesale price."
          :with_close_btn="false"
        />
      </div>
    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <ResponsiveButton
        button_type="outlined"
        built_in_theme="error"
        size="medium"
        display_name="Cancel"
        @click-button="handleClose"
      />
      <ResponsiveButton
        button_type="filled"
        built_in_theme="dark"
        size="medium"
        display_name="Save"
        :disabled="!canSave || saving"
        @click-button="handleSave"
      />
    </template>
  </Dialog>
</template>

<script>
import { postMarkUps } from '@/api/markup';
import { notify } from '@/utils/notify';

export default {
  name: 'BatchEditDialog',


  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    providerOptions: {
      type: Array,
      default: () => [],
    },
    currentProviderId: {
      type: [Number, String],
      default: null,
    },
    isClinic: {
      type: Boolean,
      default: false,
    },
    clinicId: {
      type: [Number, String],
      default: null,
    },
    customerId: {
      type: [Number, String],
      default: null,
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
  },

  emits: ['update:modelValue', 'updateTableRows'],

  data() {
    return {
      saving: false,

      // Provider selection — array of provider values, bound to MultiSelector v-model
      selectedProviderValues: [],

      // Customization
      allowCustomization: true,
      showCustomizationInfo: true,

      // Price update
      priceUpdateType: 'no_markup',
      priceValue: '',
      priceError: false,

      // Batch options: no fixed_price for multiple tests (same as VA Portal)
      batchPriceUpdateOptions: [
        { value: 'no_markup', label: 'No Price Update' },
        { value: 'fixed_profit', label: 'Price Update by Revenue' },
        { value: 'added_percentage', label: 'Price Update by Percentage' },
      ],
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    priceInputLabel() {
      switch (this.priceUpdateType) {
        case 'fixed_profit':
          return 'Amount';
        case 'added_percentage':
          return 'Percentage';
        default:
          return '';
      }
    },

    priceInputPlaceholder() {
      switch (this.priceUpdateType) {
        case 'fixed_profit':
          return `e.g. ${this.currencySymbol}5`;
        case 'added_percentage':
          return 'e.g. 10%';
        default:
          return '';
      }
    },

    currentPrefix() {
      switch (this.priceUpdateType) {
        case 'fixed_profit':
          return `+${this.currencySymbol}`;
        case 'added_percentage':
          return '+';
        default:
          return '';
      }
    },

    isPriceValid() {
      if (this.priceUpdateType === 'no_markup') return true;
      if (!this.priceValue) return true;

      const value = parseFloat(this.priceValue);
      if (isNaN(value) || value < 0) return false;

      // For batch: validate against ALL selected products
      if (this.priceUpdateType === 'added_percentage') {
        return value >= 0 && value <= 50;
      }
      // For fixed_profit: check each product's retail stays within 100%-150%
      if (this.priceUpdateType === 'fixed_profit') {
        return this.selectedProducts.every(product => {
          const wholesale = product.wholeSalePrice || product.basePrice || 0;
          return value <= wholesale * 0.5;
        });
      }
      return true;
    },

    canSave() {
      // Provider is required
      if (this.isClinic) {
        if (this.selectedProviderValues.length === 0) {
          return false;
        }
      } else {
        if (!this.currentProviderId && !this.customerId) {
          return false;
        }
      }
      // Must enter value when markup type requires it
      if (this.priceUpdateType !== 'no_markup' && !this.priceValue) {
        return false;
      }
      return true;
    },
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.initializeDialog();
      }
    },

    priceValue(newVal) {
      // Sanitize: only digits and decimal point
      if (typeof newVal === 'string') {
        const sanitized = newVal.replace(/[^\d.]/g, '');
        if (sanitized !== newVal) {
          this.priceValue = sanitized;
          return;
        }
      }
      this.validatePrice();
    },

    priceUpdateType() {
      this.validatePrice();
    },
  },

  methods: {
    initializeDialog() {
      this.saving = false;
      this.priceError = false;
      this.showCustomizationInfo = true;
      this.priceUpdateType = 'no_markup';
      this.priceValue = '';
      this.allowCustomization = true;

      // Pre-select current provider into MultiSelector (v-model is an array of values)
      const preselectOption = this.currentProviderId != null
        ? this.providerOptions.find(opt => String(opt.value) === String(this.currentProviderId))
        : null;
      this.selectedProviderValues = preselectOption ? [preselectOption.value] : [];
    },

    handlePriceTypeChange() {
      this.priceValue = '';
      this.priceError = false;
    },

    validatePrice() {
      if (this.priceUpdateType === 'no_markup' || !this.priceValue) {
        this.priceError = false;
        return;
      }
      this.priceError = !this.isPriceValid;
    },

    handleClose() {
      this.dialogVisible = false;
    },

    getItemType(productType) {
      switch (productType) {
        case 'va':
        case 'vw':
          return 'packagePriceId';
        case 'promotion':
          return 'oldPromotionId';
        case 'favorites':
          return 'shortcutId';
        case 'discount':
        case 'custom':
        default:
          return 'bundleId';
      }
    },

    async handleSave() {
      if (!this.canSave) return;

      if (this.priceUpdateType !== 'no_markup' && !this.isPriceValid) {
        this.priceError = true;
        return;
      }

      if (this.isClinic && this.selectedProviderValues.length === 0) {
        notify({ type: 'error', message: 'Please select at least one provider.' });
        return;
      }

      this.saving = true;

      try {
        // Determine affiliate_ids
        const affiliateIds = this.isClinic
          ? this.selectedProviderValues.map(v => Number(v))
          : [Number(this.currentProviderId || this.customerId)];

        // Format markup value (percentage needs /100)
        const markupValue = this.priceUpdateType === 'added_percentage'
          ? parseFloat(this.priceValue) / 100
          : parseFloat(this.priceValue) || 0;

        // Build markup_schemes: each affiliate gets all selected products
        const markupSchemes = {};
        affiliateIds.forEach(affId => {
          const entries = [];
          this.selectedProducts.forEach(product => {
            const itemType = this.getItemType(product.type);
            // Handle products with multiple underlying IDs (VW deduped)
            const ids = Array.isArray(product.ids) && product.ids.length ? product.ids : [product.id];
            ids.forEach(idVal => {
              entries.push({
                item_type: itemType,
                item_id: String(idVal),
                item_name: product.name,
                markup_type: this.priceUpdateType,
                markup_value: markupValue,
                changeable: this.allowCustomization,
                customized_name: null,
              });
            });
          });
          markupSchemes[affId] = entries;
        });

        const payload = {
          account_type: this.isClinic ? 'clinic' : 'customer',
          account_id: this.isClinic ? Number(this.clinicId) : Number(this.customerId),
          clinic_id: Number(this.clinicId),
          customer_id: Number(this.customerId),
          markup_schemes: markupSchemes,
        };

        await postMarkUps(payload);

        // Emit update event for parent to update table rows
        this.$emit('updateTableRows', {
          products: this.selectedProducts,
          affiliateIds,
          markUpType: this.priceUpdateType,
          markUpValue: markupValue,
          changable: this.allowCustomization,
        });

        notify({
          type: 'success',
          title: 'Success',
          message: `Patient prices updated for ${this.selectedProducts.length} ${this.selectedProducts.length === 1 ? 'test' : 'tests'}.`,
        });

        this.handleClose();
      } catch (error) {
        console.error('Failed to batch update retail prices:', error);
        notify({
          type: 'error',
          title: 'Error',
          message: 'Failed to update patient prices. Please try again.',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.section {
  margin-bottom: 12px;
}

.section-divider {
  height: 1px;
  background: #E6E6E6;
  margin: 12px 0;
}

.form-field {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4A4A4A;
  margin-bottom: 6px;
}

.customization-row .field-label {
  display: inline-block;
  margin-bottom: 0;
  margin-right: 8px;
}

.required {
  color: #D10000;
  font-weight: 500;
}

:deep(.combo-box-suffix) {
  display: flex;
  align-items: center;
}

.customization-field {
  margin-top: 12px;
}

.customization-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toggle-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.customization-label {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.toggle-label {
  color: var(--SecondaryBlack, #666);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.info-alert, .error-alert {
  margin-top: 8px;
}

/* Switch styling - match single edit dialog */
.customization-switch {
  --el-switch-on-color: var(--IconGrey, #374957);
  --el-switch-off-color: #fff;
}

.customization-switch :deep(.el-switch__core) {
  width: 40px;
  min-width: 40px;
  height: 22px;
  border-radius: 13px;
  padding: 2px;
  border: 1px solid var(--IconGrey, #374957);
}

.customization-switch :deep(.el-switch__action) {
  width: 18px;
  height: 18px;
  background: var(--IconGrey, #374957);
  border-radius: 50%;
  left: 2px;
}

.customization-switch.is-checked :deep(.el-switch__action) {
  left: calc(100% - 20px);
  background: linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%);
}

.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.alert-content span {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
}

.alert-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  flex-shrink: 0;
}

.alert-close:hover {
  opacity: 1;
}

/* Prefix/suffix text inside the new TextInput */
.price-input-prefix,
.price-input-suffix {
  color: var(--IconGrey, #374957);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
}

/* Error border for the new TextInput */
.price-input-error :deep(.text-input) {
  border-color: #D10000 !important;
}

/* SingleSelector full-width inside the dialog */
.price-section :deep(.single-selector-unit-wrapper) {
  width: 100%;
}

/* Body wrapper inside Dialog's default slot — also acts as a scoped
   marker we can match from a global rule below to override Dialog's
   default overflow-y:auto on its body (which clips dropdowns). */
.batch-edit-body {
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  transition: opacity 0.2s ease;
}

.batch-edit-body.is-saving {
  opacity: 0.5;
  pointer-events: none;
}
</style>

